/**
 * AuthProvider für Dashboard und Verwaltung — Authentik (Entscheidung 011) mit Dev-Weiche.
 *
 * VITE_AUTH_MODE=dev       → keine Anmeldung, Dev-Identität mit Admin-Rolle.
 * VITE_AUTH_MODE=authentik → Login-Gate gegen login.rmnc.app (Muster apps/stammdaten,
 *                            Logto-Zweig entfällt). OIDC-Kern in authentikClient.ts.
 *
 * Das Terminal (/terminal) liegt außerhalb dieses Providers: dort meldet man sich per
 * RFID-Chip an, nicht bei Authentik.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { AUTH_MODE, setReloginHandler, setTokenProvider } from "./authStore"
import {
  abmelden,
  accessToken,
  fehlendeKonfiguration,
  istAbmeldung,
  rollenAusClaims,
  userManager,
  type User as AkSitzung,
} from "./authentikClient"

export const ROLLE_ADMIN = "app:betriebstagebuch:admin"

export interface AuthUser {
  sub: string
  name: string
  roles: string[]
}

interface AuthContextValue {
  user: AuthUser | null
  istAdmin: boolean
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue>({ user: null, istAdmin: false, signOut: () => {} })

export function useAuth() {
  return useContext(AuthContext)
}

function adminPruefen(user: AuthUser | null): boolean {
  return !!user && (user.roles.includes("admin") || user.roles.includes(ROLLE_ADMIN))
}

const RETURN_TO_KEY = "auth:returnTo"

function Hinweis({ titel, text, aktion }: { titel: string; text: ReactNode; aktion?: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-page p-6">
      <div className="max-w-md space-y-3 text-center">
        <h1 className="text-lg font-semibold">{titel}</h1>
        <p className="text-sm text-ink2">{text}</p>
        {aktion}
      </div>
    </main>
  )
}

function Laden({ text }: { text: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-page">
      <div className="flex flex-col items-center gap-3 text-ink2">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink border-t-transparent" />
        <span className="text-sm">{text}</span>
      </div>
    </main>
  )
}

function CallbackSeite() {
  const navigate = useNavigate()
  const [fehler, setFehler] = useState(false)
  useEffect(() => {
    userManager()
      .signinRedirectCallback()
      .then(() => {
        const ziel = sessionStorage.getItem(RETURN_TO_KEY) ?? "/"
        sessionStorage.removeItem(RETURN_TO_KEY)
        navigate(ziel, { replace: true })
      })
      .catch(() => setFehler(true))
  }, [navigate])
  if (fehler) {
    return (
      <Hinweis
        titel="Anmeldung fehlgeschlagen"
        text="Die Anmeldung wurde abgebrochen oder konnte nicht abgeschlossen werden."
        aktion={
          <button
            type="button"
            onClick={() => void userManager().signinRedirect()}
            className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-surface"
          >
            Erneut anmelden
          </button>
        }
      />
    )
  }
  return <Laden text="Anmeldung wird abgeschlossen…" />
}

function AuthentikGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [sitzung, setSitzung] = useState<AkSitzung | null>(null)
  const [geladen, setGeladen] = useState(false)
  const istCallback = location.pathname === "/callback"

  useEffect(() => {
    setTokenProvider(accessToken)
    return () => setTokenProvider(null)
  }, [])

  useEffect(() => {
    setReloginHandler(() => {
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
      return userManager().signinRedirect()
    })
    return () => setReloginHandler(null)
  }, [location.pathname, location.search])

  useEffect(() => {
    const um = userManager()
    const geladenBei = (u: AkSitzung) => setSitzung(u)
    const weg = () => setSitzung(null)
    um.events.addUserLoaded(geladenBei)
    um.events.addUserUnloaded(weg)
    void um.getUser().then((u) => {
      setSitzung(u && !u.expired ? u : null)
      setGeladen(true)
    })
    return () => {
      um.events.removeUserLoaded(geladenBei)
      um.events.removeUserUnloaded(weg)
    }
  }, [])

  useEffect(() => {
    if (istCallback || !geladen || sitzung || istAbmeldung()) return
    sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
    void userManager().signinRedirect()
  }, [istCallback, geladen, sitzung, location.pathname, location.search])

  if (istCallback) return <CallbackSeite />
  if (!sitzung) {
    return <Laden text={geladen ? "Weiterleitung zur Anmeldung…" : "Anmeldung wird geprüft…"} />
  }
  const profil = sitzung.profile as Record<string, unknown>
  const user: AuthUser = {
    sub: String(profil.sub ?? ""),
    name: String(profil.name ?? profil.preferred_username ?? profil.sub ?? ""),
    roles: rollenAusClaims(profil),
  }
  return (
    <AuthContext.Provider value={{ user, istAdmin: adminPruefen(user), signOut: abmelden }}>
      {children}
    </AuthContext.Provider>
  )
}

const DEV_USER: AuthUser = { sub: "dev-admin", name: "Dev Admin", roles: ["admin"] }

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === "dev") {
    return (
      <AuthContext.Provider value={{ user: DEV_USER, istAdmin: true, signOut: () => {} }}>
        {children}
      </AuthContext.Provider>
    )
  }
  const fehlend = fehlendeKonfiguration()
  if (fehlend.length > 0) {
    return (
      <Hinweis
        titel="Anmeldung nicht konfiguriert"
        text={
          <>
            Der Build wurde ohne Anmelde-Konfiguration erstellt. Es fehlen:{" "}
            <code>{fehlend.join(", ")}</code>. Bitte an die Administration wenden.
          </>
        }
      />
    )
  }
  return <AuthentikGate>{children}</AuthentikGate>
}

/** Bereich nur für Verwaltung (reine UX-Sperre; durchgesetzt wird im Backend). */
export function NurAdmin({ children }: { children: ReactNode }) {
  const { istAdmin } = useAuth()
  if (!istAdmin) {
    return (
      <p className="p-6 text-sm text-ink2">
        Kein Zugriff auf diesen Bereich — es fehlt die Rolle <code>{ROLLE_ADMIN}</code>.
      </p>
    )
  }
  return <>{children}</>
}
